import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { store } from './state/store';
import {
  createWidgetStore,
  WidgetOrchestratorContext,
  WidgetShadowContext,
  WidgetStoreContext,
} from './state/createWidgetStore';
import type { WidgetStore } from './state/createWidgetStore';
import type { WidgetState } from './types';
import { getDirection } from './i18n';
import { generateThemeVars } from './styles/theme';
import baseStyles from './styles/base.css?inline';
import animationStyles from './styles/animations.css?inline';
import componentStyles from './styles/components.css?inline';
import fullscreenStyles from './styles/fullscreen.css?inline';
import inputBarStyles from './components/inputBar/InputBar.css?inline';
import drivingModeCss from './components/drivingMode/DrivingMode.css?inline';
import messageListCss from './components/messageList/MessageList.css?inline';
import { FloatingButton } from './components/FloatingButton.tsx';
import { ChatPanel } from './components/ChatPanel.tsx';
import { DrivingMode, cleanupDrivingMode } from './components/drivingMode/DrivingMode.tsx';
import { useMcpWidgetRuntime } from './McpChatWidget.helpers.ts';
import { syncI18nLocale } from './utils/locale';
import { WidgetLifecycleApi } from './interfaces/widgetLifecycleApiInterface.ts';
import { McpChatWidgetHandle } from './interfaces/mcpChatWidgetHandle.ts';
import { McpChatWidgetProps } from './interfaces/mcpChatWidgetProps.ts';
import { webchatApi } from './services/api';





function buildWidgetStyles(primaryColor: string): string {
  return `:host { ${generateThemeVars(primaryColor)} }
${animationStyles}
${baseStyles}
${fullscreenStyles}
${componentStyles}
${inputBarStyles}
${messageListCss}
${drivingModeCss}`;
}

function useWidgetMountLifecycle(
  props: McpChatWidgetProps,
  widgetRuntime: Pick<
    WidgetLifecycleApi,
    'syncAttributesToConfig' | 'handleSessionExpiry' | 'checkHealth' | 'closeMcpSession' | 'resetTransientState'
  >,
  lastActivityTimeRef: React.MutableRefObject<number>,
  panelWasOpenRef: React.MutableRefObject<boolean>,
): void {
  const {
    syncAttributesToConfig,
    handleSessionExpiry,
    checkHealth,
    closeMcpSession,
    resetTransientState,
  } = widgetRuntime;

  useEffect(() => {
    syncAttributesToConfig(props);
    lastActivityTimeRef.current = Date.now();

    const markActivity = () => {
      lastActivityTimeRef.current = Date.now();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'focus',
      'touchstart',
    ];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, markActivity, true);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const inactivityTimer = setInterval(() => {
      const state = store.getState();
      const timeoutMs = state.config.sessionTimeout * 60 * 1000;

      // Keep the session alive while the user is in an active conversation flow.
      if (
        state.isLoading
        || !!state.pendingConfirmation
        || state.drivingMode.active
        || state.drivingMode.listening
        || state.drivingMode.processing
        || state.drivingMode.speaking
      ) {
        markActivity();
        return;
      }

      if (Date.now() - lastActivityTimeRef.current > timeoutMs) {
        handleSessionExpiry();
        lastActivityTimeRef.current = Date.now();
      }
    }, 30_000);

    void checkHealth();

    return () => {
      void closeMcpSession();
      resetTransientState();
      clearInterval(inactivityTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, markActivity, true);
      }

      store.setState({
        isOpen: false,
        isExpanded: false,
        isLoading: false,
        error: null,
        tools: [],
        toolCalls: [],
        mcpSessionId: null,
        fileUpload: { file: null, uploading: false, progress: 0, error: null },
      });
      panelWasOpenRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function useWidgetPropSync(
  props: McpChatWidgetProps,
  widgetRuntime: Pick<
    WidgetLifecycleApi,
    'syncAttributesToConfig' | 'clearServerAuthOverride' | 'updateGreetingForLanguage' | 'checkHealth'
  >,
  prevPropsRef: React.MutableRefObject<McpChatWidgetProps | null>,
): void {
  const {
    syncAttributesToConfig,
    clearServerAuthOverride,
    updateGreetingForLanguage,
    checkHealth,
  } = widgetRuntime;

  useEffect(() => {
    const prevProps = prevPropsRef.current;
    prevPropsRef.current = props;

    if (!prevProps) {
      return;
    }

    const prevLanguage = store.getState().config.language;
    if (props.widgetId !== prevProps.widgetId) {
      clearServerAuthOverride();
    }

    syncAttributesToConfig(props);

    if (props.language !== prevProps.language && props.language !== prevLanguage) {
      updateGreetingForLanguage();
    }

    if (props.widgetId !== prevProps.widgetId) {
      void checkHealth();
    }
  }, [
    checkHealth,
    clearServerAuthOverride,
    props.primaryColor,
    props.position,
    props.title,
    props.greeting,
    props.otpGreeting,
    props.language,
    props.serverUrl,
    props.auth,
    props.sessionTimeout,
    props.displayMode,
    props.widgetId,
    props.preview,
    props.portalMode,
    syncAttributesToConfig,
    updateGreetingForLanguage,
  ]);
}


function useWidgetRenderState(
  widgetStoreRef: React.MutableRefObject<WidgetStore | null>,
  lastActivityTimeRef: React.MutableRefObject<number>,
): WidgetState {
  const [renderState, setRenderState] = useState<WidgetState>(() => store.getState());
  const pendingStateRef = useRef(renderState);
  const previousStateRef = useRef(renderState);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    widgetStoreRef.current?.setState(renderState as never);
  }, [renderState, widgetStoreRef]);

  useEffect(() => {
    const scheduleRender = (nextState: WidgetState) => {
      pendingStateRef.current = nextState;
      if (frameRef.current !== null) return;

      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        setRenderState(pendingStateRef.current);
      });
    };

    const unsubscribe = store.subscribe(() => {
      const prevState = previousStateRef.current;
      const nextState = store.getState();
      previousStateRef.current = nextState;
      lastActivityTimeRef.current = Date.now();
      widgetStoreRef.current?.setState(nextState as never);

      if (shouldSkipReactRender(prevState, nextState)) return;
      scheduleRender(nextState);
    });

    return () => {
      unsubscribe();
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [lastActivityTimeRef, widgetStoreRef]);

  return renderState;
}

function shouldSkipReactRender(
  previousState: WidgetState,
  nextState: WidgetState,
): boolean {
  if (previousState.drivingMode.active !== nextState.drivingMode.active) {
    return false;
  }

  if (previousState.config.drivingModeEnabled !== nextState.config.drivingModeEnabled) {
    return false;
  }

  if (previousState.pendingConfirmation !== nextState.pendingConfirmation) {
    return false;
  }

  if (previousState.isExpanded !== nextState.isExpanded || previousState.isOpen !== nextState.isOpen) {
    return false;
  }

  return shouldSkipStreamingRender(nextState);
}

function shouldSkipStreamingRender(state: WidgetState): boolean {
  if (!state.isLoading) return false;
  const lastMessage = state.messages[state.messages.length - 1];
  return !!lastMessage?.isStreaming;
}

export const McpChatWidget = forwardRef<McpChatWidgetHandle, McpChatWidgetProps>(
  function McpChatWidget(props, ref) {
    const { shadow } = props;

    const widgetStoreRef = useRef<WidgetStore | null>(null);
    if (!widgetStoreRef.current) {
      widgetStoreRef.current = createWidgetStore();
      widgetStoreRef.current.setState(store.getState() as never);
    }

    const panelWasOpenRef = useRef(false);
    const lastActivityTimeRef = useRef(Date.now());
    const prevPropsRef = useRef<McpChatWidgetProps | null>(null);
    const previousAuthStatusRef = useRef(store.getState().auth.status);
    const widgetRuntime = useMcpWidgetRuntime(props);
    const {
      orchestratorRef,
      resetConversation,
      refreshConfig,
      getMcpReadyPromise,
      loadConversation,
      toggleDrivingMode,
      syncAttributesToConfig,
      handleSessionExpiry,
      checkHealth,
      closeMcpSession,
      resetTransientState,
      clearServerAuthOverride,
      updateGreetingForLanguage,
      seedGreeting,
      initMcpSession,
      handleDrop,
    } = widgetRuntime;

    const state = useWidgetRenderState(widgetStoreRef, lastActivityTimeRef);

    useEffect(() => {
      syncI18nLocale(state.config.language);
    }, [state.config.language]);

    const logout = async () => {
      try { await webchatApi.authLogout(); } catch { /* cookie cleared client-side regardless */ }
      store.setAuth({ status: 'unauthenticated', email: null, token: null });

      if (store.getState().config.authProvider === 'none') {
        void checkHealth();
      }
    };

    useImperativeHandle(ref, () => ({
      resetConversation,
      refreshConfig,
      getOrchestrator: () => orchestratorRef.current,
      getMcpReadyPromise,
      getAuthToken: () => store.getState().auth.token,
      isAuthenticated: () => store.getState().auth.status === 'authenticated',
      getConversationId: () => store.getState().conversationId,
      getWidgetTitle: () => store.getState().config.title || '',
      getUserEmail: () => store.getState().auth.email,
      getDirection: () => getDirection(),
      loadConversation,
      toggleDrivingMode,
      logout,
    }), [getMcpReadyPromise, loadConversation, logout, orchestratorRef, refreshConfig, resetConversation, toggleDrivingMode]);

    useWidgetMountLifecycle(
      props,
      {
        syncAttributesToConfig,
        handleSessionExpiry,
        checkHealth,
        closeMcpSession,
        resetTransientState,
      },
      lastActivityTimeRef,
      panelWasOpenRef,
    );

    useWidgetPropSync(
      props,
      {
        syncAttributesToConfig,
        clearServerAuthOverride,
        updateGreetingForLanguage,
        checkHealth,
      },
      prevPropsRef,
    );

    useEffect(() => {
      const widgetStore = widgetStoreRef.current;
      if (!widgetStore) return undefined;

      store.attachZustandMirror((partial) => {
        widgetStore.getState().patchState(partial);
      });

      return () => store.attachZustandMirror(null);
    }, []);

    useEffect(() => {
      if (orchestratorRef.current) {
        orchestratorRef.current.updateShadow(shadow);
      }
    }, [orchestratorRef, shadow]);

    const dir = getDirection();
    const isFullscreen = state.config.displayMode === 'fullscreen';
    const isPortal = !!props.portalMode;
    const isPanelVisible = state.isOpen;

    useEffect(() => {
      if (isFullscreen && !store.getState().isOpen) {
        store.setState({ isOpen: true });
      }
    }, [isFullscreen]);

    useEffect(() => {
      if (isPortal || !store.getState().drivingMode.active) return;

      cleanupDrivingMode();
      store.setDrivingMode({
        active: false,
        listening: false,
        speaking: false,
        processing: false,
        transcript: '',
        lastResponse: '',
        error: null,
        keypadOpen: false,
        userGender: undefined,
      });
    }, [isPortal]);

    useEffect(() => {
      const previousAuthStatus = previousAuthStatusRef.current;
      previousAuthStatusRef.current = state.auth.status;

      if (
        previousAuthStatus === 'authenticated'
        && state.auth.status !== 'authenticated'
        && store.getState().drivingMode.active
      ) {
        cleanupDrivingMode();
        store.setDrivingMode({
          active: false,
          listening: false,
          speaking: false,
          processing: false,
          transcript: '',
          lastResponse: '',
          error: null,
          keypadOpen: false,
          userGender: undefined,
        });
      }

      if (previousAuthStatus === 'authenticated' && state.auth.status !== 'authenticated') {
        (shadow.host as HTMLElement).dispatchEvent(
          new CustomEvent('mcp-chat:unauthenticated', {
            bubbles: true,
            composed: true,
          }),
        );
      }

      if (previousAuthStatus !== 'authenticated' && state.auth.status === 'authenticated') {
        (shadow.host as HTMLElement).dispatchEvent(
          new CustomEvent('mcp-chat:authenticated', {
            bubbles: true,
            composed: true,
            detail: { email: state.auth.email },
          }),
        );
      }
    }, [shadow, state.auth.email, state.auth.status]);

    useEffect(() => {
      if (!isPanelVisible) return;

      seedGreeting();

      if (state.auth.status === 'authenticated') {
        void initMcpSession();
      }
    }, [initMcpSession, isPanelVisible, seedGreeting, state.auth.status]);

    const isFirstOpen = isPanelVisible && !panelWasOpenRef.current;
    panelWasOpenRef.current = isPanelVisible;

    const isDrivingMode = isPortal
      && state.auth.status === 'authenticated'
      && state.config.drivingModeEnabled
      && state.drivingMode.active;
    const allStyles = buildWidgetStyles(state.config.primaryColor);

    return (
      <WidgetStoreContext.Provider value={widgetStoreRef.current}>
          <WidgetShadowContext.Provider value={shadow}>
          <WidgetOrchestratorContext.Provider value={orchestratorRef}>
            <>
              <style>{allStyles}</style>
              <div
                className={`mcp-widget-root${isFullscreen ? ' fullscreen-mode' : ''}${isPortal ? ' portal-mode' : ''}`}
                dir={dir}
              >
                {!isFullscreen && !isPortal && <FloatingButton />}
                {isPanelVisible && (
                  <ChatPanel
                    isFullscreen={isFullscreen}
                    isFirstOpen={isFirstOpen}
                    isPortal={isPortal}
                    onDrop={handleDrop}
                  />
                )}
              </div>
              {isDrivingMode && <DrivingMode />}
            </>
          </WidgetOrchestratorContext.Provider>
        </WidgetShadowContext.Provider>
      </WidgetStoreContext.Provider>
    );
  },
);
